// frontend/src/pages/admin/ProductCategoryForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Checkbox } from 'antd'; // Removed 'message' import
import api from '../../utils/api'; 
import { useNotificationApi } from '../../context/NotificationContext'; // <--- NEW IMPORT

const ProductCategoryForm = ({ visible, onCancel, onSuccess, productCategory }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const notificationApi = useNotificationApi(); // <--- NEW: Get notification API from context

  // Helper function to show errors (persistent notification, top-right)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key); 
    try {
      notificationApi.error({
        key, 
        message: title,
        description: content,
        duration: 4.5, 
        placement: 'topRight',
        style: {
          width: 400, 
          zIndex: 10000, 
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({ 
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]); 

  // Helper function to show success (transient notification, top-right)
  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key, 
      message: title,
      description: description,
      duration: 3, 
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000, 
      },
    });
  }, [notificationApi]);

  // Helper function to show loading (sticky notification, top-right, with delay and min display time)
  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300; 
    const minDisplayTime = 500; 

    const startTime = Date.now();
    
    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0, 
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000, 
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer); 
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        if (dismissNotification) { 
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Effect to populate form fields when editing an existing product category
  useEffect(() => {
    if (!visible) {
      form.resetFields(); 
      return;
    }

    if (productCategory) {
      console.log('ProductCategoryForm: Initializing form for editing product category:', productCategory);
      form.setFieldsValue({
        name: productCategory.name,
        code: productCategory.code,
        isActive: Boolean(productCategory.isActive), 
        description: productCategory.description || null, // Ensure description is set
      });
    } else {
      console.log('ProductCategoryForm: Resetting form fields for new product category.');
      form.resetFields();
      form.setFieldsValue({
          isActive: true // New categories are active by default (camelCase)
      });
    }
  }, [visible, productCategory, form]);

  // Handle form submission (add new or update existing)
  const handleSubmit = async () => {
    let hideLoading; 
    try {
      setLoading(true);
      const loadingKey = 'productCategorySubmitLoading';
      hideLoading = showLoading(loadingKey, productCategory ? 'Updating product category...' : 'Creating product category...'); 

      const values = await form.validateFields(); 
      console.log('ProductCategoryForm: Form values validated:', values);

      const payload = {
        name: values.name,
        code: values.code,
        isActive: values.isActive, 
        description: values.description || null, // Include description in payload
      };

      if (productCategory) {
        console.log('ProductCategoryForm: Attempting to update product category:', productCategory.id, payload);
        await api.put(`/api/product-categories/${productCategory.id}`, payload);
        showSuccess('Product Category Updated', 'Product category details updated successfully!', 'productCategoryUpdateSuccess'); 
      } else {
        console.log('ProductCategoryForm: Attempting to create new product category:', payload);
        await api.post('/api/product-categories', payload);
        showSuccess('Product Category Created', 'New product category created successfully!', 'productCategoryCreateSuccess'); 
      }

      console.log('ProductCategoryForm: API response successful.');
      // Introduce a small delay before closing the modal and dismissing loading
      setTimeout(() => {
        if (hideLoading && typeof hideLoading === 'function') hideLoading(); 
        onSuccess(true); // Call onSuccess with true to indicate action triggered refresh
        onCancel(); 
      }, 500); 

    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading(); 
      setLoading(false);
      console.error('ProductCategoryForm: Form submission error:', error.response?.data || error.message, error);
      showError('Submission Failed', error.response?.data?.message || 'Failed to save product category.', 'productCategoryFormSubmitError'); 
    } finally {
      setLoading(false); 
    }
  };

  return (
    <Modal
      open={visible} 
      title={productCategory ? 'Edit Product Category' : 'Add New Product Category'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}> {/* Disable cancel button while loading */}
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={600} 
      destroyOnClose={true} 
    >
      <Form
        form={form}
        layout="vertical"
        name="product_category_form"
        initialValues={{ isActive: true }} 
      >
        <Form.Item
          name="name"
          label="Category Name"
          rules={[
            { required: true, message: 'Please input the category name!' },
            { type: 'string', min: 2, message: 'Category name must be at least 2 characters.' }
          ]}
        >
          <Input placeholder="e.g., Infrastructure Software" />
        </Form.Item>
        <Form.Item
          name="code"
          label="Category Code"
          rules={[
            { required: true, message: 'Please input the category code!' },
            { type: 'string', min: 2, max: 10, message: 'Category code must be between 2 and 10 characters.' }
          ]}
        >
          <Input placeholder="e.g., ISW" />
        </Form.Item>
        <Form.Item // NEW: Added Description Form.Item
          name="description"
          label="Description"
        >
          <Input.TextArea rows={3} placeholder="e.g., Software related to IT infrastructure management." />
        </Form.Item>
        <Form.Item
          name="isActive" 
          label="Status"
          valuePropName="checked" 
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProductCategoryForm;